// src/App.tsx - Updated with public routes
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { Toaster } from "react-hot-toast";
import { store, RootState } from "./redux/store";
import { checkAuthStatus } from "./redux/slices/authSlice";
import Layout from "./components/Layout";
import { Sparkles } from "lucide-react";

// Public Pages (no authentication required)
import Home from "./pages/Home";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";

// Private Pages (authentication required)
import Dashboard from "./pages/Dashboard";
import Wallet from "./pages/Wallet";
import Transactions from "./pages/Transactions";
import Deposit from "./pages/Deposit";
import Withdrawal from "./pages/Withdrawal";
import Kyc from "./pages/Kyc";
import Recovery from "./pages/Recovery";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminKyc from "./pages/admin/AdminKyc";
import AdminRecovery from "./pages/admin/AdminRecovery";
import Profile from "./pages/Profile";
import { isAdmin, hasWalletConnected } from "./types";
import AdminTransactions from "./pages/admin/AdminTransactions";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import { setTheme } from "./redux/slices/themeSlice";

// Loading Component
const AppLoading: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 flex items-center justify-center">
    <div className="text-center">
      <div className="relative">
        <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-12">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto"></div>
            <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
          </div>
          <p className="mt-6 text-gray-600 dark:text-gray-400 font-medium">
            Loading application...
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Unverified Route Component (for verify-email page)
const UnverifiedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { token, user, isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  // If user is already verified and authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  // If no token or user, redirect to login
  if (!token || !user) {
    return <Navigate to="/login" />;
  }

  // If user exists but not verified, allow access to verification page
  if (!user.email_verified_at) {
    return <>{children}</>;
  }

  return <Navigate to="/dashboard" />;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin(user)) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

// KYC Protected Route
const KycProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  if (user.verification_level < 2) {
    return <Navigate to="/kyc" />;
  }

  return <>{children}</>;
};

// Wallet Protected Route
const WalletProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  if (!hasWalletConnected(user)) {
    return <Navigate to="/wallet" />;
  }

  return <>{children}</>;
};

// Public Route Component (no authentication required)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  // If user is authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const dispatch = useDispatch();
  const { isAppLoading } = useSelector((state: RootState) => state.auth);
  const { mode } = useSelector((state: RootState) => state.theme);

  useEffect(() => {
    // Set theme on app load
    document.documentElement.classList.toggle("dark", mode === "dark");

    // Listen for storage events to sync theme across tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "theme") {
        const newTheme = event.newValue;
        if (newTheme === "light" || newTheme === "dark") {
          dispatch(setTheme(newTheme));
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Check auth status on app load
    dispatch(checkAuthStatus() as any);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch, mode]);

  if (isAppLoading) {
    return <AppLoading />;
  }

  return (
    <div className={mode}>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 transition-all duration-300">
        <Router>
          <Routes>
            {/* Public Routes (no authentication required) */}
            <Route
              path="/"
              element={
                <PublicRoute>
                  <Home />
                </PublicRoute>
              }
            />
            <Route
              path="/about"
              element={
                <PublicRoute>
                  <About />
                </PublicRoute>
              }
            />
            <Route
              path="/privacy"
              element={
                <PublicRoute>
                  <Privacy />
                </PublicRoute>
              }
            />
            <Route
              path="/terms"
              element={
                <PublicRoute>
                  <Terms />
                </PublicRoute>
              }
            />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/verify-email"
              element={
                <UnverifiedRoute>
                  <VerifyEmail />
                </UnverifiedRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />

            {/* Protected Routes (authentication required) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/wallet"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Wallet />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Transactions />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/deposit"
              element={
                <ProtectedRoute>
                  <KycProtectedRoute>
                    <WalletProtectedRoute>
                      <Layout>
                        <Deposit />
                      </Layout>
                    </WalletProtectedRoute>
                  </KycProtectedRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/withdraw"
              element={
                <ProtectedRoute>
                  <KycProtectedRoute>
                    <WalletProtectedRoute>
                      <Layout>
                        <Withdrawal />
                      </Layout>
                    </WalletProtectedRoute>
                  </KycProtectedRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/kyc"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Kyc />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/recovery"
              element={
                <ProtectedRoute>
                  <KycProtectedRoute>
                    <WalletProtectedRoute>
                      <Layout>
                        <Recovery />
                      </Layout>
                    </WalletProtectedRoute>
                  </KycProtectedRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <Layout>
                    <AdminUsers />
                  </Layout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/kyc"
              element={
                <AdminRoute>
                  <Layout>
                    <AdminKyc />
                  </Layout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/recovery"
              element={
                <AdminRoute>
                  <Layout>
                    <AdminRecovery />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/transactions"
              element={
                <AdminRoute>
                  <Layout>
                    <AdminTransactions />
                  </Layout>
                </AdminRoute>
              }
            />

            {/* Redirect unknown routes to home page */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background:
                mode === "dark"
                  ? "rgba(31, 41, 55, 0.9)"
                  : "rgba(255, 255, 255, 0.9)",
              color: mode === "dark" ? "#FFFFFF" : "#1F2937",
              border: `1px solid ${mode === "dark" ? "rgba(55, 65, 81, 0.5)" : "rgba(229, 231, 235, 0.5)"}`,
              backdropFilter: "blur(12px)",
              borderRadius: "12px",
              padding: "16px",
              boxShadow:
                "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            },
            success: {
              style: {
                background:
                  mode === "dark"
                    ? "rgba(16, 185, 129, 0.9)"
                    : "rgba(16, 185, 129, 0.9)",
                color: "#FFFFFF",
                border: `1px solid ${mode === "dark" ? "rgba(5, 150, 105, 0.5)" : "rgba(110, 231, 183, 0.5)"}`,
              },
              iconTheme: {
                primary: "#FFFFFF",
                secondary: mode === "dark" ? "#065F46" : "#10B981",
              },
            },
            error: {
              style: {
                background:
                  mode === "dark"
                    ? "rgba(239, 68, 68, 0.9)"
                    : "rgba(239, 68, 68, 0.9)",
                color: "#FFFFFF",
                border: `1px solid ${mode === "dark" ? "rgba(185, 28, 28, 0.5)" : "rgba(252, 165, 165, 0.5)"}`,
              },
              iconTheme: {
                primary: "#FFFFFF",
                secondary: mode === "dark" ? "#991B1B" : "#EF4444",
              },
            },
            loading: {
              style: {
                background:
                  mode === "dark"
                    ? "rgba(107, 114, 128, 0.9)"
                    : "rgba(107, 114, 128, 0.9)",
                color: "#FFFFFF",
                border: `1px solid ${mode === "dark" ? "rgba(75, 85, 99, 0.5)" : "rgba(156, 163, 175, 0.5)"}`,
              },
            },
          }}
        />
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App;
